'use client';

import { API_URL } from '../constants';
import { createClient } from '../supabase/client';

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public statusText?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function getAuthToken(): Promise<string> {
  const supabase = createClient();
  const { data: sessionData, error } = await supabase.auth.getSession();
  if (error || !sessionData?.session) {
    throw new ApiError('Unable to get Supabase session');
  }

  const token = sessionData.session.access_token;
  if (!token) {
    throw new ApiError('No access token found in Supabase session');
  }

  return token;
}

export function buildHeaders(includeContentType: boolean = true, token?: string): Record<string, string> {
  const headers: Record<string, string> = {};

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

export async function authenticatedFetch(endpoint: string, options?: RequestInit): Promise<Response> {
  try {
    const token = await getAuthToken();
    const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: buildHeaders(true, token),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(`Request failed: ${errorText}`, response.status, response.statusText);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Request failed', undefined, error?.toString());
  }
}

export async function unauthenticatedFetch(endpoint: string, options?: RequestInit): Promise<Response> {
  try {
    const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: buildHeaders(true),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(`Request failed: ${errorText}`, response.status, response.statusText);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Request failed', undefined, error?.toString());
  }
}

export async function put<T>(endpoint: string, id: string, data: unknown): Promise<T> {
  const response = await authenticatedFetch(`${endpoint}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.json();
}
